import {
  Button,
  Chip,
  Css,
  FilterDefs,
  Filters,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  RowStyles,
  ScrollableContent,
  SimpleHeaderAndData,
  Tag,
  column,
  multiFilter,
  simpleDataRows,
  simpleHeader,
  usePersistedFilter,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { createDesignPackageUrl, createDesignPackagesUrl } from "src/RouteUrls";
import { SearchBox, formatDate } from "src/components";
import { DesignPackageFilter, DesignPackageLocation, ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useReadyPlanStatusFilter } from "src/hooks/filters/readyPlanStatus/useReadyPlanStatusFilter";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import {
  DesignPackageTableItemFragment,
  useDesignCatalogsTableQuery,
} from "src/routes/libraries/design-catalog/DesignCatalogsPage.generated";
import { isDefined, queryResult, readyPlanStatusToTagType } from "src/utils";
import { DesignPackageButtonMenu } from "./design-package-configurator/components/DesignPackageButtonMenu";
import { DesignPackageLocationBadge, designUpgradeTagColor, planPackageTagColor } from "./designCatalogAtoms";
import { useNewDesignCatalogWizard } from "./new-design-catalog-wizard/useNewDesignCatalogWizard";

type DesignPackagesPageFilter = DesignPackageFilter & { location?: DesignPackageLocation[] };

export function DesignCatalogsPage() {
  const openNewDesignCatalogWizard = useNewDesignCatalogWizard();
  const [search, setSearch] = useState("");
  const { rpStatusFilter: status } = useReadyPlanStatusFilter({ isDPFilter: true });
  const locationFilter = useMemo(
    () =>
      multiFilter({
        label: "Type",
        options: [
          { value: DesignPackageLocation.Interior, label: "Interior" },
          { value: DesignPackageLocation.Exterior, label: "Exterior" },
        ],
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.label,
      }),
    [],
  );
  const filterDefs: FilterDefs<DesignPackagesPageFilter> = useMemo(
    () => ({ status, location: locationFilter }),
    [status, locationFilter],
  );
  const { filter, setFilter } = usePersistedFilter<DesignPackagesPageFilter>({
    filterDefs,
    storageKey: "design-package-filter",
  });
  const designCatalogsQuery = useDesignCatalogsTableQuery({ variables: { filter: { status: filter.status } } });
  return (
    <div css={Css.h100.w100.$}>
      <PageHeader
        title="Design Packages"
        breadcrumb={[{ href: createDesignPackagesUrl(), label: "Design Packages" }]}
        right={<Button label="Create New" onClick={openNewDesignCatalogWizard} />}
      />
      <TableActions>
        <Filters<DesignPackagesPageFilter> filter={filter} onChange={setFilter} filterDefs={filterDefs} />
        <SearchBox onSearch={setSearch} />
      </TableActions>
      <ScrollableContent>
        {queryResult(designCatalogsQuery, (data) => (
          <DesignCatalogsTable
            designPackages={data.designPackages.entities.filter(
              (dp) => !filter.location?.nonEmpty || filter.location.includes(dp.location.code),
            )}
            search={search}
          />
        ))}
      </ScrollableContent>
    </div>
  );
}

type DesignCatalogsPageProps = { designPackages: DesignPackageTableItemFragment[]; search: string };

function DesignCatalogsTable({ designPackages, search }: DesignCatalogsPageProps) {
  const navigate = useNavigate();
  return (
    <div css={Css.py3.$}>
      <GridTable
        rows={createRows(designPackages)}
        columns={createColumns(navigate)}
        rowStyles={createRowStyles()}
        sorting={{ on: "client", initial: ["name", "ASC"] }}
        filter={search}
      />
    </div>
  );
}

type Row = SimpleHeaderAndData<DesignPackageTableItemFragment>;

function createRowStyles(): RowStyles<Row> {
  return { data: { rowLink: ({ id, data }) => createDesignPackageUrl(id, data.aggregateLatest.id) } };
}

function createRows(items: DesignPackageTableItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...simpleDataRows(items)];
}

function createColumns(navigate: NavigateFunction): GridColumn<Row>[] {
  return [
    column<Row>({
      id: "location",
      header: "Type",
      data: (designPackage) => ({
        content: () => <DesignPackageLocationBadge designPackage={designPackage} />,
        value: designPackage.location.name,
      }),
      w: "100px",
      align: "right",
    }),
    column<Row>({
      id: "name",
      header: "Name",
      data: (designPackage) => ({
        content: () => (
          <div css={Css.df.gap1.aic.hPx(50).$}>
            {designPackage.status.code === ReadyPlanStatus.Archived && (
              <Tag text="Archived" type={readyPlanStatusToTagType[designPackage.status.code]} />
            )}
            <div css={Css.mdSb.$}>{designPackage.name}</div>
            <div>{designPackage.code}</div>
            {designPackage.availableUpdates.nonEmpty && (
              <IconButton
                inc={2}
                bgColor={Palette.Yellow200}
                icon="arrowFromBottom"
                tooltip={designPackage.availableUpdates
                  .map((u) => `${u.planVersion.readyPlan.name} - v${u.planVersion.version}`)
                  .join(", ")}
                onClick={() =>
                  navigate(createDesignPackageUrl(designPackage.id, designPackage.aggregateLatest.id, true))
                }
              />
            )}
          </div>
        ),
        value: designPackage.name,
      }),
      w: 2.0,
      align: "left",
    }),
    column<Row>({
      id: "options",
      header: "Options",
      data: (designPackage) => {
        const baseRpos = designPackage.options.filter((rpo) => rpo.globalOption.group.forDesignPackages);
        const upgradeRpos = designPackage.options.filter((rpo) => rpo.globalOption.group.forDesignUpgrade);
        const planRpos = designPackage.options.filter((rpo) => rpo.globalOption.isPlanSpecific);

        return (
          <div css={Css.df.fdr.aic.gapPx(4).$}>
            {Object.entries(baseRpos.groupBy((rpo) => rpo.globalOption.group.id)).map(([gogId, rpos]) => {
              const bgColor = rpos.first!.globalOption.group.designPackageTagColor;
              return (
                <Chip
                  key={gogId}
                  title={`${rpos.length} ${rpos.first!.globalOption.group.name} options`}
                  text={String(rpos.length)}
                  xss={Css.if(isDefined(bgColor)).bgColor(bgColor!).white.$}
                />
              );
            })}
            {upgradeRpos.nonEmpty && (
              <Chip
                text={upgradeRpos.length.toString()}
                title={`${upgradeRpos.length} upgrades`}
                xss={Css.bgColor(designUpgradeTagColor).white.$}
              />
            )}
            {planRpos.nonEmpty && (
              <Chip
                text={planRpos.length.toString()}
                title={`${planRpos.length} plan options`}
                xss={Css.bgColor(planPackageTagColor).white.$}
              />
            )}
          </div>
        );
      },
      clientSideSort: false,
    }),
    column<Row>({
      id: "updated",
      header: "Updated",
      data: (designPackage) => ({
        content: () => <div>Updated on {formatDate(designPackage.updatedAt)}</div>,
        value: formatDate(designPackage.updatedAt),
        sortValue: designPackage.updatedAt,
      }),
      align: "right",
    }),
    column<Row>({
      id: "actions",
      header: "",
      data: (designPackage) => <DesignPackageButtonMenu designPackage={designPackage} />,
      clientSideSort: false,
      align: "right",
      w: "40px",
      wrapAction: false,
    }),
  ];
}
